import * as React from 'react';
import ChatClient from 'fk-cp-chat';
import get from 'lodash/get';
import cx from 'classnames';
import differenceInCalendarDays from 'date-fns/difference_in_calendar_days';
import PropTypes from 'prop-types';
import { connect } from 'ChatProvider';
import { getBrowserDetails } from 'utils/userAgentUtils';
import { getVisitorId } from 'utils/chatUtils';
import { getObject } from 'utils/localStorage';
import { isLoading } from 'utils/asyncStatusHelper';
import shallowEqual from 'utils/shallowEqual';
import fdpHelper from 'utils/FDPHelper';
import { getRelativeTime } from 'utils/timeUtils';
import QuickReplies from 'components/QuickReplies/QuickReplies';
import ChatProductCarousel from 'components/ChatProductCarousel/ChatProductCarousel';
import ChatProductDetails from 'components/ChatProductDetails/ChatProductDetails';
import ChatImage from 'components/ChatImage/ChatImage';
import OptionedMessage from 'components/OptionedMessage/OptionedMessage';
import Button from 'components/Button/Button';
import TypingIndicator from 'components/TypingIndicator/TypingIndicator';
import MessageBubble, { MessageBubbleTheme } from 'components/MessageBubble/MessageBubble';
import Message from 'components/Message/Message';
import InfiniteScroll from 'components/Common/InfiniteScroll/InfiniteScroll';
import ProductShareMessage from 'components/ProductShareMessage/ProductShareMessage';
import ChatStatus from 'components/ChatStatusMessage/ChatStatusMessage';
import StringWidget from 'components/StringWidget';
import { NLink as Link } from 'components/Common/ALink/ALink';
import { ChatTypeEnum } from 'pages/Chat/Chat';
import styles from './ChatMessages.css';
import ChatFeedback from '../ChatFeedback/ChatFeedback';
const DateChip = ({ messageTimeStamp }) => {
    return (<div className={cx(styles['date-chip-wrapper'])}>
			<div className={styles['date-chip']}>{getRelativeTime(messageTimeStamp, false)}</div>
		</div>);
};
const UnsupportedMessage = ({ messageId, isReceivedMessage, showBubblePointer = false }) => {
    return (<MessageBubble key={messageId} isReceivedMessage={isReceivedMessage} showBubblePointer={showBubblePointer}>
			<StringWidget className={cx(styles['unsupported-message'], {
        [styles['unsupported-sent-message']]: !isReceivedMessage
    })} strKey="chatUnsupportedMessage"/>
		</MessageBubble>);
};
export var messageTypesEnum;
(function (messageTypesEnum) {
    messageTypesEnum["MESSAGE"] = "message";
    messageTypesEnum["WIDGET_CAROUSEL"] = "widgetCarousel";
    messageTypesEnum["WIDGET"] = "widget";
    messageTypesEnum["PRODUCT_SHARE"] = "productshare";
    messageTypesEnum["TEMP_IMAGE_UPLOAD_FRAME"] = "tempImageUploadFrame";
    messageTypesEnum["IMAGE"] = "image";
    messageTypesEnum["REDIRECT_INPUT"] = "redirectInput";
    messageTypesEnum["OPTIONED_MESSAGE"] = "optionedMessage";
    messageTypesEnum["OPTIONED_RESPONSE"] = "optionedResponse";
})(messageTypesEnum || (messageTypesEnum = {}));
const { MESSAGE, WIDGET_CAROUSEL, WIDGET, PRODUCT_SHARE, OPTIONED_MESSAGE, OPTIONED_RESPONSE, TEMP_IMAGE_UPLOAD_FRAME, IMAGE, REDIRECT_INPUT } = messageTypesEnum;
const supportedMessageTypes = [
    MESSAGE,
    WIDGET_CAROUSEL,
    PRODUCT_SHARE,
    OPTIONED_MESSAGE,
    OPTIONED_RESPONSE,
    TEMP_IMAGE_UPLOAD_FRAME,
    IMAGE,
    REDIRECT_INPUT
];
export var widgetViewTypesEnum;
(function (widgetViewTypesEnum) {
    widgetViewTypesEnum["TEXT"] = "text";
    widgetViewTypesEnum["PRODUCT_DETAILS"] = "productDetails";
})(widgetViewTypesEnum || (widgetViewTypesEnum = {}));
const { TEXT, PRODUCT_DETAILS } = widgetViewTypesEnum;
const supportedViewTypes = [TEXT, PRODUCT_DETAILS];
class ChatMessages extends React.Component {
    constructor(props) {
        super(props);
        this.fetchChatMessages = (chatId, since) => {
            this.chatClient.fetchChatMessages(chatId, since);
        };
        this.loadMore = () => {
            const messages = get(this.props, `allMessages.messagesForChats.${this.props.selectedChat}`, {}) || {};
            if (isLoading(messages)) {
                return;
            }
            const messageIdList = get(this.props, `allMessages.messagesForChats.${this.props.selectedChat}.messageIdList`) || [];
            const oldestMessageId = messageIdList[messageIdList.length - 1];
            const timestampOfOldestMessage = get(this.props, `allMessages.messages.${oldestMessageId}.timeStamp`);
            this.props.fetchChatMessages(this.props.selectedChat, timestampOfOldestMessage);
        };
        this.setScrollAnchorRef = c => {
            if (typeof this.props.innerRefScrollAnchor === 'function') {
                this.props.innerRefScrollAnchor(c);
            }
        };
        this.onClose = () => {
            this.chatClient.feedbackClosedAction(this.props.selectedChat);
        };
        this.closeModal = () => {
            return this.context.modal.hide();
        };
        this.isChatDisabled = (chatDisabled, chatType, index) => {
            if (chatType !== ChatTypeEnum.CUSTOMER_SUPPORT) {
                return chatDisabled;
            }
            else {
                return chatDisabled || index !== 0;
            }
        };
        this.state = {
            connectionStatus: get(props, 'networkStatus.connectionStatus')
        };
        this.chatClient = ChatClient.getInstance();
        this.markLatestReceivedMessageAsRead(this.props);
    }
    /*
        Finds the most recent received message from list of messages for the currently selected chat. If there is such a message, calls method on ChatClient to mark it as read.
    */
    markLatestReceivedMessageAsRead(props) {
        const messages = get(props, `allMessages.messagesForChats.${props.selectedChat}`) || {};
        const { messageIdList = [] } = messages;
        const latestReceivedMessageId = messageIdList.find(id => {
            const message = get(props, ['allMessages', 'messages', id]) || {};
            const visitorId = getVisitorId();
            const { requestingVisitorId } = message;
            return requestingVisitorId && visitorId !== requestingVisitorId;
        });
        if (latestReceivedMessageId) {
            const latestReceivedMessage = get(props, ['allMessages', 'messages', latestReceivedMessageId]) || {};
            const lastReadMessageIdentifiers = get(this.props, 'allChats.messageSummary.lastReadMessageIdentifiers');
            const { chatId } = latestReceivedMessage;
            const lastReadMessageData = (lastReadMessageIdentifiers || []).find(item => item.chatId === chatId) || {};
            const { messageId } = lastReadMessageData;
            if (latestReceivedMessageId !== messageId) {
                this.chatClient.updateLastReadMessage(latestReceivedMessageId, chatId);
            }
        }
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        /*
            If the tab's or window's focus state has changed then mark the last received message as read or if new messages have been received while the chat window was already active, mark the last received message as read.
        */
        if (!this.props.isVisible && nextProps.isVisible) {
            this.markLatestReceivedMessageAsRead(nextProps);
        }
        else if (nextProps.isVisible) {
            this.markLatestReceivedMessageAsRead(nextProps);
        }
        const prevConnectionStatus = get(this.props, 'networkStatus.connectionStatus');
        const connectionStatus = get(nextProps, 'networkStatus.connectionStatus');
        if (prevConnectionStatus !== connectionStatus) {
            this.setState({ connectionStatus });
        }
        if (nextProps.allMessages.feedbackResponse && nextProps.allMessages.feedbackResponse[nextProps.selectedChat]) {
            this.context.modal.show({
                children: (<ChatFeedback selectedChat={nextProps.selectedChat} closeModal={this.closeModal} responseBody={nextProps.allMessages.feedbackResponse[nextProps.selectedChat].decodedMessageBody}/>),
                closeButton: true,
                closeOnOverlayClick: true,
                onClose: this.onClose
            });
        }
    }
    componentDidMount() {
        const context = get(this.props, 'selectedChatMeta.chatMeta.context', '');
        if (this.props.selectedChat && context) {
            this.sendChatWindowOpenEvent(this.props.selectedChat, context);
        }
    }
    sendChatWindowOpenEvent(chatId, context) {
        try {
            const parsedContext = JSON.parse(context);
            // Send ChatProductInfo object for brand chat only
            if (parsedContext.features) {
                fdpHelper.trackEvent({
                    en: 'CWO',
                    cid: chatId,
                    cpi: {
                        brand: parsedContext.features.brand || '',
                        seller: parsedContext.features.sellerId || '',
                        fsn: parsedContext.features.lid || '',
                        vertical: parsedContext.features.vertical || '',
                        price: parsedContext.features.price || ''
                    }
                });
            }
            else {
                fdpHelper.trackEvent({
                    en: 'CWO',
                    cid: chatId
                });
            }
        }
        catch (_e) {
            return;
        }
    }
    shouldComponentUpdate(nextProps, nextState) {
        // Prevents re-render if only focus state has changed.
        if (this.props.isVisible !== nextProps.isVisible) {
            return false;
        }
        return !shallowEqual(this.props, nextProps) || this.state.connectionStatus !== nextState.connectionStatus;
    }
    isPlainMessage(message) {
        let decodedMessage;
        try {
            decodedMessage = JSON.parse(message.decodedMessageBody);
        }
        catch (error) {
            return false;
        }
        const messageType = decodedMessage.type;
        if ([OPTIONED_RESPONSE, MESSAGE].indexOf(messageType) > -1) {
            return true;
        }
        else if (messageType === WIDGET) {
            const viewType = decodedMessage.viewType;
            return viewType === TEXT;
        }
        return false;
    }
    isMessageTypeSupported(message) {
        let decodedMessage;
        try {
            decodedMessage = JSON.parse(message.decodedMessageBody);
        }
        catch (error) {
            return false;
        }
        const messageType = decodedMessage.type;
        if (messageType === WIDGET) {
            const viewType = decodedMessage.viewType;
            return supportedViewTypes.indexOf(viewType) > -1;
        }
        return supportedMessageTypes.indexOf(messageType) > -1;
    }
    shouldRenderDateChip(index) {
        const messages = get(this.props, `allMessages.messagesForChats.${this.props.selectedChat}`) || {};
        const { messageIdList = [], hasMore = true } = messages;
        const messageId = messageIdList[index];
        const message = get(this.props, ['allMessages', 'messages', messageId]) || {};
        const messageTimeStamp = message.timeStamp || message.userTimeStamp;
        if (index === messageIdList.length - 1) {
            return !hasMore;
        }
        else {
            const nextMessageId = messageIdList[index + 1];
            const nextMessage = get(this.props, ['allMessages', 'messages', nextMessageId]) || {};
            const nextMessageTimeStamp = nextMessage.timeStamp || nextMessage.userTimeStamp;
            return differenceInCalendarDays(new Date(messageTimeStamp), new Date(nextMessageTimeStamp)) > 0;
        }
    }
    /*
        Logic to show-hide the chat message bubble pointer explained.
        Same type of messages are shown grouped together in the UI. We show the bubble pointer only for the first message in the group.

    */
    shouldShowBubblePointer(index) {
        const messages = get(this.props, `allMessages.messagesForChats.${this.props.selectedChat}`) || {};
        const { messageIdList = [] } = messages;
        const messageId = messageIdList[index];
        // If this message is the last one, show the pointer.
        if (index === messageIdList.length - 1) {
            return true;
        }
        else {
            const message = get(this.props, ['allMessages', 'messages', messageId]) || {};
            const nextMessage = get(this.props, ['allMessages', 'messages', messageIdList[index + 1]]) || {};
            const chatCredentials = getObject('chatCredentials') || {};
            const { visitorId } = chatCredentials;
            const { requestingVisitorId } = message;
            const isReceivedMessage = (requestingVisitorId && requestingVisitorId !== visitorId) || false;
            const nextMessageRequestingVisitorId = nextMessage.requestingVisitorId;
            const isNextMessageReceivedMessage = (nextMessageRequestingVisitorId && nextMessageRequestingVisitorId !== visitorId) || false;
            // If this message is sent one and the next one is received or vice versa, show.
            if (isReceivedMessage !== isNextMessageReceivedMessage) {
                return true;
            }
            else {
                // If there's a break in messages because of data chip, show.
                if (this.shouldRenderDateChip(index)) {
                    return true;
                }
                // if this and the next message types aren't supported by the client, they are essentially same message for the client, don't show.
                if (!this.isMessageTypeSupported(message) && !this.isMessageTypeSupported(nextMessage)) {
                    return false;
                }
                // Different message types are rendered as plain text message, if this and next one are plain messages, don't show.
                if (this.isPlainMessage(message) && this.isPlainMessage(nextMessage)) {
                    return false;
                }
                let decodedMessage;
                try {
                    decodedMessage = JSON.parse(message.decodedMessageBody);
                }
                catch (error) {
                    return true;
                }
                const messageType = decodedMessage.type;
                let decodedNextMessage;
                try {
                    decodedNextMessage = JSON.parse(nextMessage.decodedMessageBody);
                }
                catch (error) {
                    return true;
                }
                const nextMessageType = decodedNextMessage.type;
                // If message type changes between this message and next, show.
                return messageType !== nextMessageType;
            }
        }
    }
    renderPlainMessage(messageContent, messageId, isReceivedMessage, timeStamp, message, index) {
        return (<MessageBubble showBubblePointer={this.shouldShowBubblePointer(index)} key={messageId} isReceivedMessage={isReceivedMessage}>
				<Message lastReadMessageTimestamp={this.props.lastReadMessageTimestamp} lastDeliveredMessageTimestamp={this.props.lastDeliveredMessageTimestamp} messageStatus={message.messageStatus} messageContent={messageContent} isReceivedMessage={isReceivedMessage} timeStamp={timeStamp}/>
			</MessageBubble>);
    }
    render() {
        const messages = get(this.props, `allMessages.messagesForChats.${this.props.selectedChat}`) || {};
        const { messageIdList = [], hasMore = true } = messages;
        const { connectionStatus } = this.state;
        const browserDetails = getBrowserDetails();
        const isFirefox = browserDetails.name === 'Firefox';
        return (<div className={styles['chat-messages-wrapper']}>
				<ChatStatus connectionStatus={connectionStatus}/>
				<InfiniteScroll loaderProps={{
            width: 3,
            className: styles['infinite-scroll-loader-class']
        }} innerRef={this.props.innerRef} className={cx({
            [styles['firefox-fix']]: isFirefox
        }, styles['chat-messages'])} hasMore={hasMore} loadMore={this.loadMore}>
					<div ref={this.setScrollAnchorRef}/>
					<div className={styles['padding-element']}/>
					<div className={cx(styles['typing-indicator-container'], {
            [styles['typing']]: this.props.isTyping,
            [styles['typing-indicator-container-position-firefox-fix']]: isFirefox,
            [styles['typing-indicator-container-position']]: !isFirefox
        })}>
						<TypingIndicator />
					</div>
					{messageIdList.map((messageId, index) => {
            // TODO: import type for message from fk-cp-chat.
            const message = get(this.props, ['allMessages', 'messages', messageId]) || {};
            const { requestingVisitorId, chatType } = message;
            let decodedMessage;
            // TODO: Move the JSON parse to fk-cp-chat library.
            try {
                decodedMessage = JSON.parse(message.decodedMessageBody);
            }
            catch (error) {
                return null;
            }
            const messageType = decodedMessage.type;
            const viewType = decodedMessage.viewType;
            const timeStamp = message.timeStamp || message.userTimeStamp;
            const chatCredentials = getObject('chatCredentials');
            const visitorId = chatCredentials.visitorId;
            const isReceivedMessage = (requestingVisitorId && requestingVisitorId !== visitorId) || false;
            let messageElement;
            switch (messageType) {
                case IMAGE: {
                    const file = this.props.files.find(file => {
                        return message.imageId && file.id === message.imageId;
                    });
                    messageElement = (<ChatImage key={messageId} lastReadMessageTimestamp={this.props.lastReadMessageTimestamp} lastDeliveredMessageTimestamp={this.props.lastDeliveredMessageTimestamp} messageStatus={message.messageStatus} isReceivedMessage={isReceivedMessage} file={file} timeStamp={timeStamp} data={decodedMessage}/>);
                    break;
                }
                case TEMP_IMAGE_UPLOAD_FRAME: {
                    const file = this.props.files.find(file => {
                        return message.imageId && file.id === message.imageId;
                    });
                    messageElement = (<ChatImage retryUpload={this.props.retryUpload} key={messageId} lastReadMessageTimestamp={this.props.lastReadMessageTimestamp} lastDeliveredMessageTimestamp={this.props.lastDeliveredMessageTimestamp} messageStatus={message.messageStatus} isReceivedMessage={isReceivedMessage} file={file} timeStamp={timeStamp} data={decodedMessage}/>);
                    break;
                }
                case REDIRECT_INPUT:
                    const url = get(decodedMessage, 'action.params.url');
                    const desktopUrl = get(decodedMessage, 'action.params.desktopUrl');
                    messageElement = (<MessageBubble key={messageId} className={styles['redirect-input-bubble']} showBubblePointer={this.shouldShowBubblePointer(index)} isReceivedMessage={isReceivedMessage}>
										<Message showMessageMeta={false} messageStatus={message.messageStatus} messageContent={decodedMessage.text} isReceivedMessage={isReceivedMessage} timeStamp={timeStamp}/>
										<Link to={desktopUrl || url}>
											<Button primary className={styles['redirect-button']}>
												{decodedMessage.buttonText}
											</Button>
										</Link>
									</MessageBubble>);
                    break;
                case OPTIONED_MESSAGE:
                    messageElement = (<MessageBubble key={messageId} showBubblePointer={this.shouldShowBubblePointer(index)} className={styles['optioned-messages-bubble']} isReceivedMessage={isReceivedMessage}>
										<OptionedMessage disable={this.isChatDisabled(this.props.chatDisabled, chatType, index)} messageSendCallback={this.props.messageSendCallback} selectedChat={this.props.selectedChat} messageId={messageId} data={decodedMessage}/>
									</MessageBubble>);
                    break;
                case PRODUCT_SHARE: {
                    const messageContent = decodedMessage.shareableProductValue;
                    messageElement = (<MessageBubble key={messageId} showBubblePointer={this.shouldShowBubblePointer(index)} isReceivedMessage={isReceivedMessage} theme={MessageBubbleTheme.LIGHT}>
										<ProductShareMessage lastReadMessageTimestamp={this.props.lastReadMessageTimestamp} lastDeliveredMessageTimestamp={this.props.lastDeliveredMessageTimestamp} messageStatus={message.messageStatus} messageContent={messageContent} isReceivedMessage={isReceivedMessage} timeStamp={timeStamp}/>
									</MessageBubble>);
                    break;
                }
                case WIDGET: {
                    switch (viewType) {
                        case PRODUCT_DETAILS:
                            messageElement = (<MessageBubble key={messageId} showBubblePointer={this.shouldShowBubblePointer(index)}>
												<ChatProductDetails messageSendCallback={this.props.messageSendCallback} selectedChat={this.props.selectedChat} data={decodedMessage.widgetComponents}/>
											</MessageBubble>);
                            break;
                        case TEXT:
                            const messageContent = get(decodedMessage, [
                                'widgetComponents',
                                0,
                                'data',
                                'value',
                                'text'
                            ]);
                            messageElement = this.renderPlainMessage(messageContent, messageId, isReceivedMessage, timeStamp, message, index);
                            break;
                        default:
                            messageElement = (<UnsupportedMessage showBubblePointer={this.shouldShowBubblePointer(index)} isReceivedMessage={isReceivedMessage} messageId={messageId}/>);
                    }
                    break;
                }
                case OPTIONED_RESPONSE: {
                    const messageContent = get(decodedMessage, 'response.data.text');
                    messageElement = this.renderPlainMessage(messageContent, messageId, isReceivedMessage, timeStamp, message, index);
                    break;
                }
                case MESSAGE: {
                    const messageContent = decodedMessage.contents;
                    messageElement = this.renderPlainMessage(messageContent, messageId, isReceivedMessage, timeStamp, message, index);
                    break;
                }
                case WIDGET_CAROUSEL:
                    const { widgets } = decodedMessage;
                    messageElement = (<ChatProductCarousel messageSendCallback={this.props.messageSendCallback} selectedChat={this.props.selectedChat} products={widgets} key={messageId}/>);
                    break;
                default:
                    messageElement = (<UnsupportedMessage showBubblePointer={this.shouldShowBubblePointer(index)} isReceivedMessage={isReceivedMessage} messageId={messageId}/>);
            }
            return (<React.Fragment key={messageId}>
								{messageElement}
								{this.shouldRenderDateChip(index) && <DateChip messageTimeStamp={timeStamp}/>}
							</React.Fragment>);
        })}
				</InfiniteScroll>
				<QuickReplies key={get(this.props, `this.props.allMessages.quickReplies.${this.props.selectedChat}.messageId`)} selectedChat={this.props.selectedChat} messageSendCallback={this.props.messageSendCallback} data={this.props.allMessages.quickReplies[this.props.selectedChat]}/>
			</div>);
    }
}
ChatMessages.contextTypes = {
    modal: PropTypes.object.isRequired
};
const mapStateToProps = state => {
    return {
        allChats: state.allChats,
        allMessages: state.allMessages,
        networkStatus: state.networkStatus
    };
};
export default connect(mapStateToProps, null)(ChatMessages);
